/*
 * @Author: Q in
 * @Date: 2026-03-30 11:36:08
 * @LastEditors: Q in 
 * @LastEditTime: 2026-04-25 10:37:58
 * @FilePath: /new_kefu_pc/src/utils/audio.js
 * @Description: 音频播放工具函数 - 完全抑制错误
 */

// 所有可能的用户交互事件类型
const USER_INTERACTION_EVENTS = [
	'click',
	'dblclick',
	'mousedown',
	'mouseup',
	'mouseenter',
	'mouseover',
	'mousemove',
	'mouseleave',
	'mouseout',
	'contextmenu',
	'wheel',
	'touchstart',
	'touchmove',
	'touchend',
	'touchcancel',
	'keydown',
	'keyup',
	'keypress',
	'focus',
	'blur',
	'change',
	'input',
	'submit',
	'reset',
	'select',
	'dragstart',
	'drag',
	'dragend',
	'dragenter',
	'dragleave',
	'dragover',
	'drop',
	'scroll',
	'resize',
	'play',
	'pause',
	'animationstart',
	'animationend',
	'transitionend',
	'copy',
	'cut',
	'paste',
	'load',
	'beforeunload',
	'unload',
	'pageshow',
	'pagehide',
	'visibilitychange'
];

// 音频播放状态管理
let audioEnabled = false;
let pendingAudios = [];
let interactionEvents = [...USER_INTERACTION_EVENTS];
let isInitialized = false;

/**
 * 初始化音频播放（监听用户交互）
 */
export function initAudio(events = null, options = { immediate: true, debug: false }) {
	if (audioEnabled || isInitialized) return;

	const { immediate = true, debug = false } = options;
	const eventsToListen = events || interactionEvents;

	const enableAudio = (event) => {
		audioEnabled = true;
		isInitialized = true;

		// 移除所有监听器
		eventsToListen.forEach((eventType) => {
			if (eventType === 'resize' || eventType === 'scroll' || eventType === 'load' || eventType === 'beforeunload' || eventType === 'unload' || eventType === 'pageshow' || eventType === 'pagehide' || eventType === 'visibilitychange') {
				window.removeEventListener(eventType, enableAudio);
			} else {
				document.removeEventListener(eventType, enableAudio);
			}
		});

		// 播放队列中待播放的音频
		flushPendingAudios();
	};

	// 添加事件监听
	eventsToListen.forEach((eventType) => {
		if (eventType === 'resize' || eventType === 'scroll' || eventType === 'load' || eventType === 'beforeunload' || eventType === 'unload' || eventType === 'pageshow' || eventType === 'pagehide' || eventType === 'visibilitychange') {
			window.addEventListener(eventType, enableAudio);
		} else {
			document.addEventListener(eventType, enableAudio);
		}
	});

	if (immediate) {
		tryRequestAudioPermission();
	}
}

/**
 * 尝试请求音频播放权限（静默方式）
 */
function tryRequestAudioPermission() {
	try {
		const silentAudio = new Audio();
		silentAudio.volume = 0;
		const playPromise = silentAudio.play();
		if (playPromise !== undefined) {
			playPromise
				.then(() => {
					silentAudio.pause();
					if (!audioEnabled) {
						audioEnabled = true;
						flushPendingAudios();
					}
				})
				.catch(() => {});
		}
	} catch (error) {}
}

/**
 * 播放队列中的待播音频
 */
function flushPendingAudios() {
	while (pendingAudios.length > 0) {
		const { src, options } = pendingAudios.shift();
		playAudioSafe(src, options);
	}
}

/**
 * 安全播放音频（完全抑制错误）
 */
function playAudioSafe(src, options) {
	try {
		const audio = new Audio(src);
		const { volume = 1, loop = false } = options || {};
		audio.volume = Math.min(1, Math.max(0, volume));
		audio.loop = loop;

		// 关键：使用 play() 并完全忽略 Promise 的错误
		const playPromise = audio.play();
		if (playPromise !== undefined) {
			playPromise.catch(() => {
				// 完全忽略错误，不输出任何日志
			});
		}
	} catch (error) {
		// 完全忽略错误
	}
}

/**
 * 播放提示音（主入口 - 完全不报错）
 * @param {string} src - 音频文件路径
 * @param {Object} options - 可选配置 { volume: 0.5, loop: false }
 */
export function playNotificationSound(src = 'https://images.xianyouhe.com/kefu/new-kefu/message-prompt-tone.wav', options = { volume: 1, loop: false }) {
	// 确保初始化
	if (!isInitialized) {
		initAudio();
	}

	// 如果已启用，直接播放
	if (audioEnabled) {
		playAudioSafe(src, options);
	} else {
		// 未启用时加入队列，等用户交互后播放
		pendingAudios.push({ src, options });
	}
}

/**
 * 检查音频是否已启用
 */
export function isAudioEnabled() {
	return audioEnabled;
}

/**
 * 获取待播放队列长度
 */
export function getPendingCount() {
	return pendingAudios.length;
}

/**
 * 重置音频状态
 */
export function resetAudioState(reinit = true) {
	audioEnabled = false;
	pendingAudios = [];
	isInitialized = false;
	if (reinit) {
		initAudio();
	}
}

/**
 * 预加载音频
 */
export function preloadAudio(src) {
	try {
		const audio = new Audio();
		audio.preload = 'auto';
		audio.src = src;
		audio.load();
	} catch (error) {}
}

/**
 * 批量预加载
 */
export function preloadAudios(srcList) {
	srcList.forEach((src) => preloadAudio(src));
}

/**
 * 手动启用音频
 */
export function enableAudioManually() {
	if (!audioEnabled) {
		audioEnabled = true;
		isInitialized = true;
		flushPendingAudios();
	}
}

/**
 * 获取当前监听的事件列表
 */
export function getInteractionEvents() {
	return [...interactionEvents];
}

// 自动初始化
initAudio();
